import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import notificationService from '../services/notificationService';

// Create the context
const NotificationContext = createContext();

// Create a provider component
export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  
  // Function to fetch notifications from the backend
  const fetchNotifications = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Try to fetch real notifications, but fall back to mock if API not ready
      try {
        const response = await notificationService.getNotifications();
        setNotifications(response.data || []);
        
        // Calculate unread count
        const unread = (response.data || []).filter(notification => !notification.read).length;
        setUnreadCount(unread);
      } catch (apiError) {
        console.warn('API not available, using mock data', apiError);
        
        // Mock data for development/testing
        const mockNotifications = [
          {
            id: '1',
            type: 'info',
            message: 'Welcome to the platform!',
            read: false,
            date: new Date(Date.now() - 1000 * 60 * 60).toISOString(),
            link: '/member/dashboard'
          },
          {
            id: '2',
            type: 'alert',
            message: 'Your subscription will expire soon',
            read: false,
            date: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
            link: '/member/subscription'
          },
          {
            id: '3',
            type: 'success',
            message: 'Payment processed successfully',
            read: true,
            date: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3).toISOString(),
            link: '/member/wallet'
          }
        ];
        
        setNotifications(mockNotifications);
        
        // Calculate unread count from mock data
        const unread = mockNotifications.filter(notification => !notification.read).length;
        setUnreadCount(unread);
      }
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };
  
  // Mark a notification as read
  const markAsRead = async (id) => {
    try {
      // Try the real API, fall back to local state update if API not ready
      try {
        await notificationService.markAsRead(id);
      } catch (apiError) {
        console.warn('API not available, updating local state only', apiError);
      }
      
      // Update local state
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === id 
            ? { ...notification, read: true } 
            : notification
        )
      );
      
      // Recalculate unread count
      const updatedNotifications = notifications.map(notification => 
        notification.id === id ? { ...notification, read: true } : notification
      );
      const unread = updatedNotifications.filter(notification => !notification.read).length;
      setUnreadCount(unread);
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  };
  
  // Mark all notifications as read
  const markAllAsRead = async () => {
    try {
      // Try the real API, fall back to local state update if API not ready
      try {
        await notificationService.markAllAsRead();
      } catch (apiError) {
        console.warn('API not available, updating local state only', apiError);
      }
      
      // Update local state
      setNotifications(prev => 
        prev.map(notification => ({ ...notification, read: true }))
      );
      
      // Reset unread count
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all notifications as read:', err);
    }
  };
  
  // Toggle the notifications popover
  const toggleNotifications = () => {
    setOpen(prev => !prev);
  };
  
  // Close the notifications popover
  const closeNotifications = () => {
    setOpen(false);
  };
  
  // Fetch notifications when the user changes
  useEffect(() => {
    if (user) {
      fetchNotifications();
      
      // Set up polling for new notifications (every 2 minutes)
      const intervalId = setInterval(fetchNotifications, 2 * 60 * 1000);
      
      // Clean up interval on unmount
      return () => clearInterval(intervalId);
    } else {
      // Reset state when user logs out
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [user]);
  
  // Provide the context value
  const value = {
    notifications,
    unreadCount,
    loading,
    error,
    open,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    toggleNotifications,
    closeNotifications
  };
  
  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

// Custom hook to use the notification context
export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

// Add alias for backward compatibility (singular version)
export const useNotification = useNotifications;

export default NotificationContext;